/**
 * ReportViewer Component
 *
 * Full-screen modal for viewing iteration reports with Markdown rendering.
 * Includes navigation sidebar for multiple iterations and file download links.
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { TaskIteration, IterationAttachment } from '@/types/tasks';
import { ITERATION_STATUS_CONFIG, ARTIFACT_TYPE_CONFIG, formatFileSize } from '@/types/tasks';
import { getIterationAttachmentDownloadUrl } from '@/services/api';

interface ReportViewerProps {
  taskId: string;
  iterations: TaskIteration[];
  initialIterationId?: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function ReportViewer({
  taskId,
  iterations,
  initialIterationId,
  isOpen,
  onClose,
}: ReportViewerProps) {
  const { t } = useTranslation('tasks');
  const [selectedIterationId, setSelectedIterationId] = useState<string | null>(
    initialIterationId || null
  );

  // Set initial selection when modal opens - only run on open, not on selection change
  useEffect(() => {
    if (isOpen) {
      if (initialIterationId) {
        setSelectedIterationId(initialIterationId);
      } else if (iterations.length > 0) {
        // Select the latest completed iteration
        const completed = iterations.filter(i => i.status === 'completed' && i.report);
        setSelectedIterationId(completed[0]?.id || iterations[0]?.id || null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialIterationId]); // Deliberately exclude iterations and selectedIterationId

  const selectedIteration = useMemo(
    () => iterations.find(i => i.id === selectedIterationId),
    [iterations, selectedIterationId]
  );

  const completedIterations = useMemo(
    () => iterations.filter(i => i.status === 'completed' && i.report),
    [iterations]
  );

  const handleDownloadAttachment = async (attachment: IterationAttachment) => {
    try {
      const { url } = await getIterationAttachmentDownloadUrl(
        taskId,
        attachment.iteration_id || '',
        attachment.id
      );
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to get download URL:', err);
      alert('Failed to download file. Please try again.');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  const outputAttachments = selectedIteration?.attachments?.filter(
    a => a.artifact_type === 'output'
  ) || [];

  const referenceAttachments = selectedIteration?.attachments?.filter(
    a => a.artifact_type === 'reference'
  ) || [];

  return (
    <div className="fixed inset-0 z-50 bg-white">
      <div className="flex h-full">
        {/* Sidebar */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t('reportViewer.title')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {t('reportViewer.count', { count: completedIterations.length, plural: completedIterations.length !== 1 ? 's' : '' })}
            </p>
          </div>

          {/* Iterations List */}
          <div className="flex-1 overflow-y-auto p-2">
            {iterations.map((iteration) => {
              const hasReport = iteration.report && iteration.report.length > 0;
              const statusConfig = ITERATION_STATUS_CONFIG[iteration.status];
              const isSelected = iteration.id === selectedIterationId;

              return (
                <button
                  key={iteration.id}
                  onClick={() => setSelectedIterationId(iteration.id)}
                  disabled={!hasReport}
                  className={`w-full text-left p-3 rounded-md mb-1 transition-colors ${
                    isSelected
                      ? 'bg-blue-100 border border-blue-200'
                      : hasReport
                      ? 'hover:bg-gray-100'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{statusConfig.icon}</span>
                    <span className="font-medium text-sm text-gray-900">
                      #{iteration.iteration_number}
                    </span>
                    <span
                      className={`px-1.5 py-0.5 text-xs rounded ${statusConfig.bgColor} ${statusConfig.color}`}
                    >
                      {t(`iterationStatus.${iteration.status}`)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {iteration.user_prompt || t('reportViewer.noReport')}
                  </p>
                  {iteration.completed_at && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatDate(iteration.completed_at)}
                    </p>
                  )}
                </button>
              );
            })}
          </div>

          {/* Close Button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('reportViewer.closeViewer')}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between">
            <div>
              {selectedIteration ? (
                <>
                  <h1 className="text-xl font-semibold text-gray-900">
                    {t('reportViewer.iterationTitle', { number: selectedIteration.iteration_number })}
                  </h1>
                  <div className="flex items-center gap-2 mt-1">
                    {selectedIteration.report_created_by && (
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        selectedIteration.report_created_by === 'ai'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {t('reportViewer.reportBy', { creator: selectedIteration.report_created_by === 'ai' ? t('reportViewer.reportByAI') : t('reportViewer.reportByUser') })}
                      </span>
                    )}
                    {selectedIteration.completed_at && (
                      <span className="text-sm text-gray-500">
                        Completed {formatDate(selectedIteration.completed_at)}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <h1 className="text-xl font-semibold text-gray-900">
                  {t('reportViewer.selectIteration')}
                </h1>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Report Content */}
          <div className="flex-1 overflow-y-auto">
            {selectedIteration?.report ? (
              <div className="max-w-4xl mx-auto p-6">
                {/* Original Request */}
                {selectedIteration.user_prompt && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">{t('reportViewer.originalRequest')}</h3>
                    <p className="text-gray-600 whitespace-pre-wrap">
                      {selectedIteration.user_prompt}
                    </p>
                  </div>
                )}

                {/* Markdown Report */}
                <div className="prose prose-blue max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {selectedIteration.report}
                  </ReactMarkdown>
                </div>

                {/* Output Files */}
                {outputAttachments.length > 0 && (
                  <div className="mt-8 p-4 bg-green-50 rounded-lg border border-green-200">
                    <h3 className="text-sm font-medium text-green-800 mb-3 flex items-center gap-2">
                      {ARTIFACT_TYPE_CONFIG.output.icon} {t('reportViewer.outputFiles', { count: outputAttachments.length })}
                    </h3>
                    <div className="space-y-2">
                      {outputAttachments.map((attachment) => (
                        <button
                          key={attachment.id}
                          onClick={() => handleDownloadAttachment(attachment)}
                          className="w-full flex items-center justify-between p-3 bg-white rounded-md border border-green-200 hover:border-green-300 hover:bg-green-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-green-600">📤</span>
                            <div className="text-left">
                              <p className="text-sm font-medium text-gray-900">
                                {attachment.file_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(attachment.file_size)}
                              </p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reference Files */}
                {referenceAttachments.length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      {ARTIFACT_TYPE_CONFIG.reference.icon} {t('reportViewer.referenceFiles', { count: referenceAttachments.length })}
                    </h3>
                    <div className="space-y-2">
                      {referenceAttachments.map((attachment) => (
                        <button
                          key={attachment.id}
                          onClick={() => handleDownloadAttachment(attachment)}
                          className="w-full flex items-center justify-between p-3 bg-white rounded-md border border-gray-200 hover:border-gray-300 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-gray-500">📎</span>
                            <div className="text-left">
                              <p className="text-sm font-medium text-gray-900">
                                {attachment.file_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(attachment.file_size)}
                              </p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="mt-2 text-sm">
                    {t('reportViewer.empty')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
